import * as React from 'react'

import { Box, Button, Item } from '@factorialco/deprecated-design-system'
import { FinanceValueObjectsTaxTypeEnumValues } from 'generated/resources/datatable.graphql'
import {
  FinanceValueObjectsTaxTypeEnum,
  UpdateFinanceTaxTypeLedgerAccountAssignmentsInput,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { FormErrors } from 'modules/core/hooks/useForm'
import { useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { countries } from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Loading from 'modules/finance/components/Loading'
import { useLedgerAccountAssignment } from 'modules/finance/hooks/accounting/useLedgerAccountAssignment'
import { useTaxTypes } from 'modules/finance/hooks/useTaxTypes'
import { LegalEntity } from 'modules/finance/pages/Accounting/TaxTypePage/types'

import LedgerAccountAssigmentForm from '../CustomCategories/LedgerAccountAssignmentForm'
import { TaxTypeTypeSelector } from '../TaxTypeTypeSelector'

interface EditTaxTypeFormValues {
  name: string
  ledgerAccountId?: number
  type?: FinanceValueObjectsTaxTypeEnum
  country_code?: string
  assignedLedgerAccounts: UpdateFinanceTaxTypeLedgerAccountAssignmentsInput[]
}

interface EditTaxTypeModalProps {
  legalEntities: LegalEntity[]
  taxTypeId: number
  seePurchaseLedgerAccounts: boolean
  seeSalesLedgerAccounts: boolean
  onClose: () => void
}

const EditTaxTypeModal: React.FC<EditTaxTypeModalProps> = ({
  legalEntities,
  taxTypeId,
  seePurchaseLedgerAccounts,
  seeSalesLedgerAccounts,
  onClose,
}) => {
  const { editTaxType, data } = useTaxTypes([taxTypeId])

  const taxType = data?.finance.taxTypes[0]

  const [loading, setLoading] = React.useState<boolean>(false)
  const countryList = countries(i18n.locale)

  const currentResourceAssignments: UpdateFinanceTaxTypeLedgerAccountAssignmentsInput[] =
    taxType?.accountResources?.map((accountResource) => ({
      ledgerAccountResourceId: accountResource.id,
      ledgerAccountId: accountResource.account.id,
      legalEntityId: accountResource.account.legalEntity.id,
      operationType: accountResource.operationType,
    })) ?? []

  const form = useForm<EditTaxTypeFormValues>({
    enableReinitialize: true,
    defaultValues: {
      name: taxType?.name ?? '',
      type: taxType?.type,
      country_code: taxType?.countryCode || undefined,
      assignedLedgerAccounts: currentResourceAssignments,
    },

    validate: (data) => {
      const errors: FormErrors<EditTaxTypeFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('finance.modals.new_tax_type.errors.name_empty')
      }

      if (!data.type || !Object.values(FinanceValueObjectsTaxTypeEnumValues).includes(data.type)) {
        errors.type = i18n.t('finance.modals.new_tax_type.errors.type_empty')
      }

      return errors
    },

    onSubmit: async (values) => {
      setLoading(true)

      const response = await editTaxType(
        taxTypeId,
        values.name,
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        values.type!,
        values.assignedLedgerAccounts,
        values.country_code
      )

      const result = handleResult(response, (errors) => {
        if (errors['name']) {
          form.setErrors({
            name: i18n.t(
              'finance.settings.taxes.tax_type.edit_tax_type_modal.messages.name_already_exist'
            ),
          })
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('finance.settings.taxes.tax_type.edit_tax_type_modal.messages.error'),
          })
        }
        track(trackingEvents.accounting.renameTaxSubmit, {
          status: 'Error',
          name: values.name,
        })
        setLoading(false)
      })

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.settings.taxes.tax_type.edit_tax_type_modal.messages.success'),
        })
        track(trackingEvents.accounting.renameTaxSubmit, {
          status: 'Success',
          name: values.name,
        })
        onClose()
      }

      return result
    },
  })

  const onSelectCountry = useUnsafeCallback(
    (item: Item) => {
      form.setValues({ ...form.values, country_code: item.value + '' })
    },
    [form]
  )

  const selectedCountry = useUnsafeMemo(
    () => countryList.find((c) => c.value === form.values.country_code),
    [countryList, form.values.country_code]
  )

  const handleSelectLedgerAccount = useLedgerAccountAssignment(form.setValues)

  return (
    <Modal
      illustration="list-document"
      title={i18n.t('finance.settings.taxes.tax_type.edit_tax_type_modal.title')}
      description={i18n.t('finance.settings.taxes.tax_type.edit_tax_type_modal.description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button submit disabled={!form.isValid || !form.dirty || loading}>
          {i18n.t('common.save')}
        </Button>
      }
    >
      <Loading loading={!taxType || loading}>
        <Box gap="s24" paddingBottom="s24">
          <TextField
            mandatory
            label={i18n.t('finance.modals.new_tax_type.fields.tax_name')}
            value={form.values.name}
            onChange={(value) => form.setValues({ ...form.values, name: value })}
            error={form.errors.name}
            readonly={taxType?.source === 'system'}
          />

          <DropdownSearchBar
            name={selectedCountry?.label || ''}
            data={countryList}
            label={i18n.t('finance.modals.new_tax_type.fields.country_selector.label')}
            labelNotFound={''}
            searchLabel={i18n.t('common.search')}
            sortLabel={i18n.t('common.name')}
            placeholder={i18n.t('finance.modals.new_tax_type.fields.country_selector.placeholder')}
            selectLabel={i18n.t('finance.modals.new_tax_type.fields.country_selector.placeholder')}
            autofocus={false}
            onSelect={onSelectCountry}
            disabled={taxType?.source === 'system'}
          />

          <TaxTypeTypeSelector
            selectedValue={form.values.type}
            onChangeType={(value) => form.setValues({ ...form.values, type: value })}
            error={form.errors.type}
            readonly={taxType?.source === 'system'}
          />
          {/* There is a seeLedgerAccounts that does this in backend
           but as we need the know each of them separately anyway we use them here*/}
          {(seePurchaseLedgerAccounts || seeSalesLedgerAccounts) && (
            <LedgerAccountAssigmentForm
              legalEntities={legalEntities}
              currentAssignments={currentResourceAssignments}
              onChange={handleSelectLedgerAccount}
              withOperationType
              seeSalesLedgerAccounts={seeSalesLedgerAccounts}
              seePurchaseLedgerAccounts={seePurchaseLedgerAccounts}
            />
          )}
        </Box>
      </Loading>
    </Modal>
  )
}

export default observer(EditTaxTypeModal)
